import React, { useEffect, useState, useCallback } from "react";

import { X, Save, RefreshCw } from "lucide-react";

import { useMsal } from "@azure/msal-react";

import { callApi } from "../api/api";

const POLICY_NUMBER_CHAR_PATTERN = /^[A-Za-z0-9\s]*$/;

function sanitizePolicyNumberInput(value) {
  return String(value || "").replace(/[^A-Za-z0-9\s]/g, "");
}

function hasValidPolicyNumberChars(value) {
  return POLICY_NUMBER_CHAR_PATTERN.test(String(value || "").trim());
}

function getLobDisplayLabel(lobCode, lobs) {
  const code = String(lobCode || "").trim();
  if (!code) return "";

  const match = lobs.find((item) => item.lookup_code === code);
  return match?.lookup_name || code;
}

export default function MetadataWorkspacePanel({
  row,
  activeTab,
  onClose,
  onSaved,
  onPreview,
  previewLoading,
  onReindex,
  isWorkPool = false,
  source = null,
}) {
  const { instance, accounts } = useMsal();

  const [form, setForm] = useState({
    document_type: "",
    policy_number: "",
    lob: "",
    agency_no: "",
    primary_insured: "",
    review_comments: "",
  });

  const [documentTypes, setDocumentTypes] = useState([]);
  const [lobs, setLobs] = useState([]);

  const [saving, setSaving] = useState(false);
  const [updated, setUpdated] = useState(false);

  const [error, setError] = useState("");
  const [originalDocumentType, setOriginalDocumentType] = useState("");

  /* =========================================
     LOAD LOOKUPS
  ========================================= */

  const loadLookups = useCallback(async () => {
    try {
      const [documentTypeRes, lobRes] = await Promise.all([
        callApi(instance, accounts, "GET", "/api/admin/lookup/document_type"),
        callApi(instance,accounts, "GET", '/api/admin/lookup/line_of_business'),
      ]);
      setDocumentTypes(documentTypeRes?.data?.data || []);
      setLobs(lobRes?.data?.data || []);
    } catch (err) {
      console.error(err);
    }
  }, [instance, accounts]);

  useEffect(() => {
    loadLookups();
  }, [loadLookups]);

  /* =========================================
     UPDATE FORM WHEN ROW CHANGES
  ========================================= */

  useEffect(() => {
    if (row) {
      const docType = row.document_type || "";
      setForm({
        document_type: docType,
        policy_number: row.policy_number || "",
        lob: row.lob || "",
        agency_no: row.agency_no || "",
        primary_insured: row.primary_insured || "",
        review_comments: row.review_comments || "",
      });
      setOriginalDocumentType(docType);

      setUpdated(false);
      setError("");
    }
  }, [row]);

  /* =========================================
     UPDATE FIELD & DERIVED STATES
  ========================================= */

  const updateField = (key, value) => {
    setForm((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  const documentTypeChanged = form.document_type !== originalDocumentType;
  const formDisabled = saving || updated;
  const hasLob = Boolean(row?.lob?.trim());

  /* =========================================
     VALIDATION & SAVE
  ========================================= */

  const handleSave = async () => {
    try {
      setSaving(true);
      setError("");

      const documentTypeChanged =
        form.document_type !== originalDocumentType;

      if (!form.document_type) {
        setError("Document Type is required");
        return;
      }

      if (!form.policy_number?.trim()) {
        setError("Policy Number is required");
        return;
      }

      if (!hasValidPolicyNumberChars(form.policy_number)) {
        setError(
          "Policy Number can only contain letters, numbers, and spaces",
        );
        return;
      }

      if (documentTypeChanged && !form.review_comments?.trim()) {
        setError("Review Comments are required when Document Type is changed");

        return;
      }
      await callApi(
        instance,
        accounts,
        "PUT",
        `/api/metadata/update/${row.msg_id}`,
        {
          ...form,
          policy_number: form.policy_number.trim(),
          isWorkPool,
          source: source || (isWorkPool ? "WORK_POOL" : "DOC_INDEX_HUB"),
        },
      );
      setUpdated(true);
      onSaved?.();
    } catch (err) {
      console.error(err);

      setError(err.message || "Failed to update metadata");
    } finally {
      setSaving(false);
    }
  };

  const update = handleSave;

  if (!row) {
    return <div style={styles.empty}>Select a record to review</div>;
  }

  return (
    <div style={styles.container}>
      {/* =========================================
          HEADER
      ========================================= */}

      <div style={styles.header}>
        <div>
          <h3 style={styles.title}>Review Panel</h3>

          <div style={styles.sub}>Review extracted claim data</div>
        </div>

        <button style={styles.closeBtn} onClick={onClose}>
          <X size={18} />
        </button>
      </div>

      {/* =========================================
          EMAIL SUMMARY
      ========================================= */}

      {/* <div style={styles.emailCard}>
        <div style={styles.emailRow}>
          <Mail size={16} />

          <div>
            <div style={styles.emailName}>
              {row.mail_from_name || row.mail_from_address || "Unknown Sender"}
            </div>

            <div style={styles.emailAddress}>{row.mail_from_address}</div>
          </div>
        </div>

        <div style={styles.subject}>{row.email_subject || "No Subject"}</div>

        <div style={styles.attachments}>
          {row.attachments?.length || 0} attachment(s)
        </div>
      </div> */}

      {/* =========================================
          FORM
      ========================================= */}

      <div style={styles.scrollableContent} className="scrollable">
        <div style={styles.formSection}>
          <Field
            label="Policy Number"
            disabled={formDisabled}
            value={form.policy_number}
            onChange={(v) =>
              updateField("policy_number", sanitizePolicyNumberInput(v))
            }
          />

          {hasLob ? (
            <Field
              label="LOB"
              disabled
              value={getLobDisplayLabel(row.lob, lobs)}
              onChange={() => {}}
            />
          ) : (
            <div>
              <label style={styles.label}>LOB</label>

              <select
                style={{
                  ...styles.input,
                  opacity: formDisabled ? 0.7 : 1,
                  background: saving ? "#f8fafc" : "#ffffff",
                  cursor: saving ? "not-allowed" : "pointer",
                }}
                disabled={formDisabled}
                value={form.lob}
                onChange={(e) => updateField("lob", e.target.value)}
              >
                <option value="">Select LOB</option>

                {lobs.map((item) => (
                  <option key={item.lookup_code} value={item.lookup_code}>
                    {item.lookup_name}
                  </option>
                ))}
              </select>
            </div>
          )}

          <Field
            label="Agency No"
            disabled={formDisabled}
            value={form.agency_no}
            onChange={(v) => updateField("agency_no", v)}
          />

          <Field
            label="Primary Insured"
            disabled={formDisabled}
            value={form.primary_insured}
            onChange={(v) => updateField("primary_insured", v)}
          />

          <div>
            <label style={styles.label}>Document Type</label>

            <select
              style={{
                ...styles.input,
                opacity: formDisabled ? 0.7 : 1,
                background: saving ? "#f8fafc" : "#ffffff",
                cursor: saving ? "not-allowed" : "pointer",
              }}
              disabled={formDisabled}
              value={form.document_type}
              onChange={(e) => updateField("document_type", e.target.value)}
            >
              <option value="">Select Document Type</option>

              {documentTypes.map((item) => (
                <option key={item.lookup_code} value={item.lookup_code}>
                  {item.lookup_name}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label style={styles.label}>
              Review Comments
              {documentTypeChanged && <span style={styles.required}>*</span>}
            </label>

            <textarea
              style={{
                ...styles.textarea,
                opacity: formDisabled ? 0.7 : 1,
                background: saving ? "#f8fafc" : "#ffffff",
                cursor: saving ? "not-allowed" : "text",
                border:
                  documentTypeChanged && !form.review_comments?.trim()
                    ? "1px solid #dc2626"
                    : "1px solid #cbd5e1",
              }}
              disabled={formDisabled}
              placeholder={
                documentTypeChanged
                  ? "Document type changed. Comments required."
                  : "Optional review comments"
              }
              value={form.review_comments}
              onChange={(e) => updateField("review_comments", e.target.value)}
            />
          </div>

          {error && <div style={styles.error}>{error}</div>}
        </div>

        {/* =========================================
          ATTACHMENTS
      ========================================= */}

        {/* <div style={styles.attachmentSection}>
          <div style={styles.sectionTitle}>Attachments</div>

          {row.attachments?.map((a, idx) => (
            <div key={idx} style={styles.attachmentCard}>
              <div style={styles.attachmentLeft}>
                <FileText size={15} />

                <div>
                  <div style={styles.attachmentName}>{a.name}</div>
                </div>
              </div>

              <button
                style={styles.previewBtn}
                onClick={() => onPreview(a.path)}
                disabled={previewLoading}
              >
                <Eye size={14} />
              </button>
            </div>
          ))}
        </div> */}
      </div>
      {/* =========================================
          FOOTER
      ========================================= */}

      <div style={styles.footer}>
        <button
          style={{
            ...styles.cancelBtn,
            opacity: saving ? 0.6 : 1,
            cursor: saving ? "not-allowed" : "pointer",
          }}
          onClick={onClose}
          disabled={formDisabled}
        >
          Cancel
        </button>

        {activeTab === "verified" ? (
          <button
            style={styles.reindexBtn}
            onClick={() => onReindex(row.msg_id)}
          >
            <RefreshCw size={15} />
            ReIndex
          </button>
        ) : (
          <button
            style={{
              ...styles.saveBtn,
              opacity: formDisabled ? 0.7 : 1,
              cursor: formDisabled ? "not-allowed" : "pointer",
            }}
            onClick={update}
            disabled={formDisabled}
          >
            <Save size={15} />

            {saving ? "Saving..." : updated ? "Updated" : "Update"}
          </button>
        )}
      </div>
    </div>
  );
}

/* =========================================
   FIELD
========================================= */

function Field({ label, value, onChange, disabled }) {
  return (
    <div>
      <label style={styles.label}>{label}</label>

      <input
        style={{
          ...styles.input,
          opacity: disabled ? 0.7 : 1,
          background: disabled ? "#f8fafc" : "#ffffff",
          cursor: disabled ? "not-allowed" : "text",
        }}
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        disabled={disabled}
      />
    </div>
  );
}

/* =========================================
   STYLES
========================================= */

const styles = {
  textarea: {
    width: "100%",
    minHeight: "68px",
    borderRadius: "8px",
    padding: "8px 12px",
    fontSize: "13px",
    outline: "none",
    resize: "vertical",
    boxSizing: "border-box",
    fontFamily: "inherit",
    border: "1px solid #cbd5e1",
  },

  required: {
    color: "#dc2626",
    marginLeft: "4px",
  },
  container: {
    width: "100%",
    height: "100%",
    minHeight: 0,
    background: "#fff",
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
    borderRadius: "16px",
  },
  scrollableContent: {
    flex: 1,
    minHeight: 0,
    overflowY: "auto",
  },

  header: {
    padding: "12px 16px",
    borderBottom: "1px solid #e2e8f0",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    flexShrink: 0,
    borderTopLeftRadius: "16px",
    borderTopRightRadius: "16px",
  },

  title: {
    margin: 0,
    fontSize: "17px",
    fontWeight: "700",
    color: "#0f172a",
  },

  sub: {
    marginTop: "2px",
    fontSize: "12px",
    color: "#64748b",
  },

  closeBtn: {
    border: "none",
    background: "transparent",
    cursor: "pointer",
    color: "#64748b",
  },

  emailCard: {
    margin: "20px",
    padding: "16px",
    border: "1px solid #e2e8f0",
    borderRadius: "14px",
    background: "#f8fafc",
  },

  emailRow: {
    display: "flex",
    gap: "10px",
    alignItems: "center",
  },

  emailName: {
    fontWeight: "600",
    color: "#0f172a",
  },

  emailAddress: {
    fontSize: "13px",
    color: "#64748b",
  },

  subject: {
    marginTop: "14px",
    color: "#0f172a",
    fontWeight: "600",
  },

  attachments: {
    marginTop: "12px",
    color: "#2563eb",
    fontWeight: "600",
    fontSize: "13px",
  },

  formSection: {
    padding: "12px 16px",
    display: "flex",
    flexDirection: "column",
    gap: "10px",
    overflowY: "auto",
  },

  label: {
    display: "block",
    marginBottom: "4px",
    fontSize: "11px",
    fontWeight: "600",
    color: "#475569",
    textTransform: "uppercase",
    letterSpacing: "0.04em",
  },

  input: {
    width: "100%",
    height: "38px",
    border: "1px solid #cbd5e1",
    borderRadius: "8px",
    padding: "0 12px",
    fontSize: "13px",
    outline: "none",
    boxSizing: "border-box",
  },

  error: {
    background: "#fee2e2",
    color: "#991b1b",
    padding: "12px",
    borderRadius: "10px",
    fontSize: "14px",
  },

  attachmentSection: {
    marginTop: "20px",
    padding: "0 20px 20px",
    maxHeight: "320px",
    overflowY: "auto",
  },

  sectionTitle: {
    fontWeight: "700",
    marginBottom: "14px",
    color: "#0f172a",
  },

  attachmentCard: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    border: "1px solid #e2e8f0",
    borderRadius: "12px",
    padding: "12px",
    marginBottom: "10px",
  },

  attachmentLeft: {
    display: "flex",
    gap: "10px",
    alignItems: "center",
    minWidth: 0,
  },

  attachmentName: {
    fontWeight: "600",
    color: "#0f172a",
  },

  attachmentPath: {
    fontSize: "11px",
    color: "#64748b",
    wordBreak: "break-all",
  },

  previewBtn: {
    width: "34px",
    height: "34px",
    borderRadius: "8px",
    border: "none",
    background: "#eff6ff",
    color: "#2563eb",
    cursor: "pointer",
  },

  footer: {
    padding: "12px 16px",
    borderTop: "1px solid #e2e8f0",
    display: "flex",
    gap: "10px",
    background: "#ffffff",
    position: "sticky",
    bottom: 0,
    zIndex: 5,
    borderBottomLeftRadius: "16px",
    borderBottomRightRadius: "16px",
  },

  cancelBtn: {
    flex: 1,
    height: "38px",
    borderRadius: "8px",
    border: "1px solid #cbd5e1",
    background: "#fff",
    cursor: "pointer",
    fontWeight: "600",
    fontSize: "13px",
  },

  saveBtn: {
    flex: 1,
    height: "38px",
    borderRadius: "8px",
    border: "none",
    background: "#2563eb",
    color: "#fff",
    cursor: "pointer",
    fontWeight: "600",
    fontSize: "13px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "6px",
  },

  reindexBtn: {
    flex: 1,
    height: "38px",
    borderRadius: "8px",
    border: "none",
    background: "#0f172a",
    color: "#fff",
    cursor: "pointer",
    fontWeight: "600",
    fontSize: "13px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "6px",
  },

  empty: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    height: "100%",
    color: "#64748b",
  },
};
